import { OWL_WINDOW_HEIGHT, OWL_WINDOW_WIDTH, } from 'maplestory-owl-transcribe-contract';
export const FREDRICK_DATA_PROTOCOL_VERSION = 1;
export const FREDRICK_DERIVED_SCHEMA_VERSION = 2;
export const FREDRICK_RESULTS_PER_PAGE = 8;
export const FREDRICK_SOURCE_IMAGE_ID_PREFIX = 'src-v1';
export const FREDRICK_EVIDENCE_IMAGE_ID_PREFIX = 'owl-v1';
export const FREDRICK_MARKET_VALIDATION_POLICY = 'fredrick-market-v1';
export const FREDRICK_OWL_PAGE_CANDIDATE_TYPE = 'owl-page-candidate';
export const FREDRICK_OWL_PAGE_REVISION_TYPE = 'owl-page-revision';
/**
 * Convert supported decoded pixels into the canonical tightly packed RGB8
 * stream used by both source-image and Owl-evidence pixel SHA-256 digests.
 *
 * RGB8 input is copied without row padding. RGBA8 input is accepted only when
 * every alpha byte is exactly 255; alpha is then stripped. Transparent or
 * partially transparent images are rejected rather than composited.
 */
export function normalizeOpaqueImageToRgb8(image) {
    assertPositiveSafeInteger(image.width, 'image width');
    assertPositiveSafeInteger(image.height, 'image height');
    if (image.format !== 'rgb8' && image.format !== 'rgba8') {
        throw new TypeError('image format must be rgb8 or rgba8');
    }
    if (!(image.data instanceof Uint8Array)
        && !(image.data instanceof Uint8ClampedArray)) {
        throw new TypeError('image data must be an unsigned 8-bit array');
    }
    const inputChannels = image.format === 'rgb8' ? 3 : 4;
    const inputRowBytes = image.width * inputChannels;
    if (!Number.isSafeInteger(inputRowBytes)) {
        throw new RangeError('image row byte length exceeds the safe integer range');
    }
    const strideBytes = image.strideBytes ?? inputRowBytes;
    if (!Number.isSafeInteger(strideBytes) || strideBytes < inputRowBytes) {
        throw new RangeError('image strideBytes must cover one complete pixel row');
    }
    const requiredInputLength = (image.height - 1) * strideBytes + inputRowBytes;
    if (!Number.isSafeInteger(requiredInputLength)) {
        throw new RangeError('image buffer length exceeds the safe integer range');
    }
    if (image.data.length < requiredInputLength) {
        throw new RangeError('image data is shorter than its dimensions and stride');
    }
    const outputLength = image.width * image.height * 3;
    if (!Number.isSafeInteger(outputLength)) {
        throw new RangeError('canonical RGB8 buffer length exceeds the safe integer range');
    }
    const output = new Uint8Array(outputLength);
    let outputOffset = 0;
    for (let y = 0; y < image.height; y += 1) {
        let inputOffset = y * strideBytes;
        for (let x = 0; x < image.width; x += 1) {
            output[outputOffset] = image.data[inputOffset];
            output[outputOffset + 1] = image.data[inputOffset + 1];
            output[outputOffset + 2] = image.data[inputOffset + 2];
            if (inputChannels === 4 && image.data[inputOffset + 3] !== 255) {
                throw new TypeError('RGBA source and evidence images must be fully opaque');
            }
            inputOffset += inputChannels;
            outputOffset += 3;
        }
    }
    return output;
}
/** Construct the canonical full-source deduplication key. */
export function createSourceImageId(source) {
    assertPositiveSafeInteger(source.width, 'source image width');
    assertPositiveSafeInteger(source.height, 'source image height');
    assertSha256Digest(source.pixelSha256, 'source image pixelSha256');
    return `${FREDRICK_SOURCE_IMAGE_ID_PREFIX}:${source.width}x${source.height}:${source.pixelSha256}`;
}
/** True when the stored source-image ID is canonical for its components. */
export function sourceImageIdMatches(source) {
    try {
        return source.sourceImageId === createSourceImageId(source);
    }
    catch {
        return false;
    }
}
export function isSourceImageId(value) {
    const match = /^src-v1:([1-9][0-9]*)x([1-9][0-9]*):(sha256:[0-9a-f]{64})$/u.exec(value);
    if (!match)
        return false;
    const width = Number(match[1]);
    const height = Number(match[2]);
    return Number.isSafeInteger(width)
        && Number.isSafeInteger(height)
        && width > 0
        && height > 0;
}
/** Construct the canonical private Owl-window pixel identity. */
export function createEvidenceImageId(pixelSha256) {
    assertSha256Digest(pixelSha256, 'evidence pixelSha256');
    return `${FREDRICK_EVIDENCE_IMAGE_ID_PREFIX}:${OWL_WINDOW_WIDTH}x${OWL_WINDOW_HEIGHT}:${pixelSha256}`;
}
/** True when the stored evidence ID is canonical for its pixel digest. */
export function evidenceImageIdMatches(evidence) {
    if (evidence.width !== OWL_WINDOW_WIDTH || evidence.height !== OWL_WINDOW_HEIGHT) {
        return false;
    }
    try {
        return evidence.evidenceImageId === createEvidenceImageId(evidence.pixelSha256);
    }
    catch {
        return false;
    }
}
export function isEvidenceImageId(value) {
    return /^owl-v1:434x317:sha256:[0-9a-f]{64}$/u.test(value);
}
export function isRetranscriptionOf(candidate, previous) {
    return candidate.capture.sourceImage.sourceImageId
        === previous.capture.sourceImage.sourceImageId
        && candidate.supersedesRevisionId === previous.revisionId;
}
export function expectedOwlPageCount(totalResults) {
    if (!Number.isSafeInteger(totalResults) || totalResults < 0) {
        throw new RangeError('totalResults must be a non-negative safe integer');
    }
    return Math.max(1, Math.ceil(totalResults / FREDRICK_RESULTS_PER_PAGE));
}
export function expectedOwlRowsOnPage(totalResults, page) {
    const pageCount = expectedOwlPageCount(totalResults);
    if (!Number.isSafeInteger(page) || page < 1 || page > pageCount) {
        throw new RangeError('page must be within the derived page range');
    }
    if (page < pageCount)
        return FREDRICK_RESULTS_PER_PAGE;
    return totalResults - (pageCount - 1) * FREDRICK_RESULTS_PER_PAGE;
}
export function transcriptionIsPartial(transcription) {
    if (transcription.itemName === null
        || transcription.page === null
        || transcription.totalPages === null
        || transcription.totalResults === null) {
        return true;
    }
    return transcription.listings.some((listing) => listing.sellerName === null
        || listing.storeName === null
        || listing.bundle === null
        || listing.price === null
        || listing.quantity === null);
}
export function isSha256Digest(value) {
    return /^sha256:[0-9a-f]{64}$/u.test(value);
}
function assertPositiveSafeInteger(value, field) {
    if (!Number.isSafeInteger(value) || value < 1) {
        throw new RangeError(`${field} must be a positive safe integer`);
    }
}
function assertSha256Digest(value, field) {
    if (!isSha256Digest(value)) {
        throw new TypeError(`${field} must be a lowercase sha256 digest`);
    }
}
//# sourceMappingURL=index.js.map